import React, {useEffect, useRef, useState} from 'react';
import { Template } from "../../../components/template/template.jsx";
import "../../../css/style-report.css";
import "../../../css/input-date-time.css";
import ReportUtils from "../../../utils/report-utils.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {Card} from "../../../components/card/card.jsx";
import {subWeeks} from "date-fns";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import Filters from "./components/filters.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import reportUtils from "../../../utils/report-utils.js";
import Content from "./components/content.jsx";
import {useQuery} from "@tanstack/react-query";
import alarmActionsService from "../../../services/reports/alarm-actions-service.js";

const initialFilters = () => {
  const refDate = new Date;
  return {
    dtStart: subWeeks(refDate, 1),
    dtEnd: refDate,
    alarmSrc: {value: "all", label: "Todos"},
    alarms: {value: "all", label: "Todos"},
    plate: "",
    archivedOccurrences: true,
    excludeAlarmsAutomaticallySigned: true,
    userLogin: "",
    userName: "",
    userCpf: ""
  }
}

const filterLabels = {
  dtStart: "Data/hora inicial",
  dtEnd: "Data/hora final",
  alarmSrc: "Origem",
  alarms: "Assinatura do alarme",
  plate: "Placa",
  archivedOccurrences: "Contabilizar ocorrências arquivadas",
  excludeAlarmsAutomaticallySigned: "Excluir alarmes assinados automaticamente",
  userLogin: "Login",
  userName: "Nome",
  userCpf: "CPF"
};

const AlarmActions = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const buttonRef = useRef(null);
  const [fixedTop, setFixedTop] = useState(null);
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [title] = useState("Ações sobre alarmes");
  const [headerSelected, setHeaderSelected] = useState(-1);
  const printComponentRef = useRef(null);
  const [canPrint, setCanPrint] = useState(false);
  const [imageLoadRevision, setImageLoadRevision] = useState(0);


  const {data: reportData = null, isLoading: isLoadingData} = useQuery({
    queryFn: () => alarmActionsService.apply(appliedFilters),
    enabled: !!appliedFilters,
    queryKey: ["data", appliedFilters]
  })

  useEffect(() => {
    if (!reportData) return;

    ReportUtils.appendFilters(
      ReportUtils.filters({
        ...filters,
        dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : "",
        dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : "",
      }, filterLabels)
    );

  }, [reportData]);

  const apply = async () => {
    const periodValidation = ReportUtils.periodValidate(filters.dtStart, filters.dtEnd, 1, "month");

    if (!periodValidation.ok) {
      ReportUtils.alertErrorMessage(periodValidation.message);
      return;
    }

    try {
      setCanPrint(false);
      setImageLoadRevision(prev => prev + 1);

      const formattedFilters = {
        dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : null,
        dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : null,
        archivedOccurrences: filters.archivedOccurrences ? "Sim" : "Não",
        excludeAlarmsAutomaticallySigned: filters.excludeAlarmsAutomaticallySigned ? "Sim" : "Não",
      };

      const auditFilters = ReportUtils.filters({
          ...filters,
          dtStart: formattedFilters.dtStart,
          dtEnd: formattedFilters.dtEnd,
          archivedOccurrences: formattedFilters.archivedOccurrences,
          excludeAlarmsAutomaticallySigned: formattedFilters.excludeAlarmsAutomaticallySigned
        },
        filterLabels, true);

      setAppliedFilters({...buildPayload(filters), filters: auditFilters});

      const scale = window.visualViewport?.scale ?? 1;
      if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

    } catch (error) {
      ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
      console.error("Erro ao gerar o relatório", error);

    }
  };

  const clearSearch = () => {
    setFilters(initialFilters());
    setAppliedFilters(null);
    setCanPrint(false);
  }

  const toggleFilters = () => {
    if (!collapsed && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setFixedTop(rect.top);
    }

    setCollapsed(prev => !prev);
  }

  useGlobalKey("Enter", apply);
  useGlobalKey("Escape", clearSearch);

  function buildPayload(data) {
    const payloadObj = {
      dtStart: reportUtils.formatDateTime(data.dtStart),
      dtEnd: reportUtils.formatDateTime(data.dtEnd),
      alarmSrc: filters.alarmSrc.value,
      alarms: filters.alarms.value
    }

    filters.archivedOccurrences && (payloadObj.archivedOccurrences = "on");
    filters.excludeAlarmsAutomaticallySigned && (payloadObj.excludeAlarmsAutomaticallySigned = "on");
    filters.userCpf && (payloadObj.userCpf = filters.userCpf.value);
    filters.userName && (payloadObj.userName = filters.userName.value);
    filters.userLogin && (payloadObj.userLogin = filters.userLogin.value);
    filters.plate && (payloadObj.plate = filters.plate);

    return payloadObj;
  }

  return (
      <div className={"flex"}>
        <Template isLoading={isLoading || isLoadingData}>
          <Template.Header>
            <div className="flex justify-between w-full">
              <Template.Title>{title}</Template.Title>
              <HeaderButtons
                exportable={false}
                exportInSheets={false}
                printable={true}
                headerChange={true}
                printComponentRef={printComponentRef}
                reportData={reportData}
                data={1}
                headerSelected={headerSelected}
                setHeaderSelected={setHeaderSelected}
                canPrint={canPrint}
              />
            </div>
          </Template.Header>
          <Template.Body className={`${collapsed ? 'p-[0]' : ''}`}>
            <Menu buttonRef={buttonRef} toggleFilters={toggleFilters} fixedTop={fixedTop}
                  collapsed={collapsed} apply={apply} clearSearch={clearSearch}>

              <Filters setFilters={setFilters} filters={filters}/>
              <AutoDatetime setFilters={setFilters}/>

            </Menu>
          </Template.Body>
        </Template>

        <Card className={`w-full h-fit mr-2 ${!reportData || reportData.length === 0  ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${!reportData || reportData.length === 0  ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
          <Card.Body>
            <div className={`flex-1 ${!reportData || reportData.length === 0 ? 'p-[0]' : 'px-[20px] py-[5px]'}`}>
              <NotAppliedContent reportData={reportData}/>
              <NoDataContent reportData={reportData} data={reportData?.length}/>
              <Content reportData={reportData} data={reportData?.length > 0} entity={headerSelected} title={title} collapsed={collapsed} setLoading={setIsLoading} printComponentRef={printComponentRef} setCanPrint={setCanPrint} imageLoadRevision={imageLoadRevision}/>
            </div>
          </Card.Body>
        </Card>
      </div>
  );
};

export default AlarmActions;